/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar2";
import Navbar from "../../components/navbar/Navbar2";
import Footer from "../../components/footer/Footer";
import { Calculator, Package, TrendingUp, TrendingDown } from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";

const emptyCosts = {
  coast: "",
  purchasePrice: "",
  packagingPrice: "",
  deliveryPrice: "",
};

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDateFromOrder = (order) => {
  const rawDate =
    order?.timeStamp ||
    order?.createdAt ||
    order?.date ||
    order?.orderDate ||
    order?.created ||
    order?.timestamp ||
    null;

  if (!rawDate) return null;

  if (rawDate?.seconds) {
    const timestampDate = new Date(rawDate.seconds * 1000);
    return Number.isNaN(timestampDate.getTime()) ? null : timestampDate;
  }

  if (typeof rawDate?.toDate === "function") {
    const timestampDate = rawDate.toDate();
    return Number.isNaN(timestampDate?.getTime?.()) ? null : timestampDate;
  }

  const parsed = new Date(rawDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isInPeriod = (orderDate, period) => {
  if (!orderDate) return false;

  const now = new Date();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  if (period === "today") {
    return orderDate >= todayStart && orderDate <= now;
  }

  if (period === "yesterday") {
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    return orderDate >= yesterdayStart && orderDate < todayStart;
  }

  if (period === "last7") {
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return orderDate >= sevenDaysAgo && orderDate <= now;
  }

  if (period === "last30") {
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return orderDate >= thirtyDaysAgo && orderDate <= now;
  }

  return true;
};

const ProfitManagement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").toLowerCase().startsWith("ar");
  const products = useSelector((state) => state.ProductsReducer || []);
  const orders = useSelector((state) => state.OrdersReducer || []);
  const [showSideBar, setShowSideBar] = useState(true);
  const [costsByProduct, setCostsByProduct] = useState({});
  const [savingProductId, setSavingProductId] = useState(null);
  const [selectedOrderPeriod, setSelectedOrderPeriod] = useState("last7");

  const availableProducts = useMemo(() => {
    return Array.isArray(products)
      ? products.filter((product) => product?.deleted !== 1)
      : [];
  }, [products]);

  useEffect(() => {
    function handleResize() {
      setShowSideBar(window.innerWidth > 1080);
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mappedFromProducts = {};

    availableProducts.forEach((product) => {
      const rawCosts = product?.profitCosts;

      let normalizedCosts = {};

      if (rawCosts && typeof rawCosts === "object" && !Array.isArray(rawCosts)) {
        normalizedCosts = rawCosts;
      } else if (Array.isArray(rawCosts)) {
        if (
          rawCosts.length === 1 &&
          rawCosts[0] &&
          typeof rawCosts[0] === "object" &&
          !Array.isArray(rawCosts[0])
        ) {
          normalizedCosts = rawCosts[0];
        } else {
          normalizedCosts = rawCosts.reduce((acc, item) => {
            if (item && typeof item === "object" && !Array.isArray(item)) {
              return { ...acc, ...item };
            }
            return acc;
          }, {});
        }
      }

      mappedFromProducts[product.id] = {
        coast: String(
          normalizedCosts?.coast ?? product?.coast ?? product?.cost ?? "",
        ),
        purchasePrice: String(
          normalizedCosts?.purchasePrice ?? product?.purchasePrice ?? "",
        ),
        packagingPrice: String(
          normalizedCosts?.packagingPrice ?? product?.packagingPrice ?? "",
        ),
        deliveryPrice: String(
          normalizedCosts?.deliveryPrice ?? product?.deliveryPrice ?? "",
        ),
      };
    });

    setCostsByProduct((prev) => ({ ...mappedFromProducts, ...prev }));
  }, [availableProducts]);

  const deliveredOrders = useMemo(() => {
    return (Array.isArray(orders) ? orders : []).filter(
      (order) =>
        order?.deleveryStatus === "5" &&
        isInPeriod(getDateFromOrder(order), selectedOrderPeriod),
    );
  }, [orders, selectedOrderPeriod]);

  const productCards = useMemo(() => {
    return availableProducts.map((product) => {
      const productName =
        product?.name ||
        product?.title ||
        product?.sku ||
        t("profitManagementPage.labels.productFallback");

      let soldQuantity = 0;
      let grossGain = 0;

      deliveredOrders.forEach((order) => {
        const orderProducts = Array.isArray(order?.products) ? order.products : [];
        if (orderProducts.length === 0) return;

        orderProducts.forEach((line) => {
          const lineName = line?.name || line?.title || "";
          const sameProductById =
            (line?.productId && product?.id && line.productId === product.id) ||
            (line?.id && product?.id && line.id === product.id);
          const sameProductByName =
            lineName && productName && lineName.toLowerCase() === productName.toLowerCase();

          if (!sameProductById && !sameProductByName) return;

          const quantity = toNumber(line?.quantity ?? line?.qty ?? 1);
          const unitPrice = toNumber(line?.price ?? line?._price ?? product?.price ?? 0);

          soldQuantity += quantity;
          grossGain += unitPrice * quantity;
        });
      });

      const savedCosts = costsByProduct[product.id] || emptyCosts;
      const unitCost =
        toNumber(savedCosts.coast || product?.coast || product?.cost || 0) +
        toNumber(savedCosts.purchasePrice || product?.purchasePrice || 0) +
        toNumber(savedCosts.packagingPrice || product?.packagingPrice || 0) +
        toNumber(savedCosts.deliveryPrice || product?.deliveryPrice || 0);

      const totalCost = unitCost * soldQuantity;
      const netGain = grossGain - totalCost;

      return {
        id: product.id,
        name: productName,
        image:
          product?.image ||
          product?.photo ||
          (Array.isArray(product?.gallery) && product.gallery.length > 0
            ? product.gallery[0]
            : ""),
        soldQuantity,
        grossGain,
        netGain,
        unitCost,
      };
    });
  }, [availableProducts, deliveredOrders, costsByProduct, t]);

  const reportTotals = useMemo(() => {
    return productCards.reduce(
      (acc, card) => {
        acc.totalSold += card.soldQuantity;
        acc.totalGross += card.grossGain;
        acc.totalNet += card.netGain;
        return acc;
      },
      { totalSold: 0, totalGross: 0, totalNet: 0 },
    );
  }, [productCards]);

  const handleCostChange = (productId, field, value) => {
    setCostsByProduct((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || emptyCosts),
        [field]: value,
      },
    }));
  };

  const handleSaveCosts = async (productId) => {
    const productCosts = costsByProduct[productId] || emptyCosts;
    const payloadCosts = {
      coast: toNumber(productCosts.coast),
      purchasePrice: toNumber(productCosts.purchasePrice),
      packagingPrice: toNumber(productCosts.packagingPrice),
      deliveryPrice: toNumber(productCosts.deliveryPrice),
    };

    try {
      setSavingProductId(productId);
      await updateDoc(doc(db, "products", productId), {
        profitCosts: payloadCosts,
      });
    } catch (error) {
      console.error("Failed to save product costs to firestore", error);
    } finally {
      setSavingProductId(null);
    }
  };

  return (
    <div
      className="container-fluid bg-gradient-to-br from-white to-purple-50 min-h-screen dark:!bg-[#141414]"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="row mb-10">
        {showSideBar && (
          <div style={{ width: "250px", flexShrink: 0 }}>
            <Sidebar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />
          </div>
        )}

        <div
          className="px-3 relative min-h-[100vh] dark:!bg-[#141414]"
          style={{ flex: 1, backgroundColor: "#fbf8fc" }}
        >
          <Navbar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />

          <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm p-4 m-3 mt-6 border border-[#fbf4ff] dark:!border-[#3c3a3d]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-[#9e68ba] mb-1 flex items-center gap-2">
                  <Calculator size={20} />
                  {t("profitManagementPage.title")}
                </h2>
                <p className="text-gray-600 dark:!text-gray-300 mb-0">
                  {t("profitManagementPage.description")}
                </p>
              </div>
            </div>
          </div>

          <div className="mx-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-4">
              <div className="text-sm text-gray-500 dark:!text-gray-400">{t("profitManagementPage.summary.totalSold")}</div>
              <div className="text-2xl font-bold text-[#9e68ba]">{reportTotals.totalSold.toFixed(0)}</div>
            </div>
            <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-4">
              <div className="text-sm text-gray-500 dark:!text-gray-400">{t("profitManagementPage.summary.totalGross")}</div>
              <div className="text-2xl font-bold text-blue-600">{reportTotals.totalGross.toFixed(2)}</div>
            </div>
            <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-4">
              <div className="text-sm text-gray-500 dark:!text-gray-400">{t("profitManagementPage.summary.totalNet")}</div>
              <div className={`text-2xl font-bold ${reportTotals.totalNet >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {reportTotals.totalNet.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="mx-3 mb-3 flex items-center justify-between">
            <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] overflow-hidden">
              <div className="flex flex-wrap">
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    selectedOrderPeriod === "today"
                      ? "bg-purple-50 dark:bg-[#522868] dark:text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                      : "text-gray-600 hover:bg-purple-50 dark:text-gray-300"
                  }`}
                  onClick={() => setSelectedOrderPeriod("today")}
                >
                  {t("profitManagementPage.period.today")}
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    selectedOrderPeriod === "yesterday"
                      ? "bg-purple-50 dark:bg-[#522868] dark:text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                      : "text-gray-600 hover:bg-purple-50 dark:text-gray-300"
                  }`}
                  onClick={() => setSelectedOrderPeriod("yesterday")}
                >
                  {t("profitManagementPage.period.yesterday")}
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    selectedOrderPeriod === "last7"
                      ? "bg-purple-50 dark:bg-[#522868] dark:text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                      : "text-gray-600 hover:bg-purple-50 dark:text-gray-300"
                  }`}
                  onClick={() => setSelectedOrderPeriod("last7")}
                >
                  {t("profitManagementPage.period.last7")}
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    selectedOrderPeriod === "last30"
                      ? "bg-purple-50 dark:bg-[#522868] dark:text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                      : "text-gray-600 hover:bg-purple-50 dark:text-gray-300"
                  }`}
                  onClick={() => setSelectedOrderPeriod("last30")}
                >
                  {t("profitManagementPage.period.last30")}
                </button>
              </div>
            </div>
          </div>

          <div className="mx-3 mb-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-y-4 gap-x-0">
            {productCards.map((card) => {
              const cardCosts = costsByProduct[card.id] || emptyCosts;
              return (
                <div
                  key={card.id}
                  className="w-full bg-white dark:!bg-[#181819] rounded-2xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] overflow-hidden"
                >
                  <div className="w-full aspect-[4/5] bg-gray-100 dark:!bg-[#222224] p-2">
                    {card.image ? (
                      <img
                        src={card.image}
                        alt={card.name}
                        className="h-full w-full object-cover rounded-xl"
                      />
                    ) : (
                      <div className="h-full w-full rounded-xl flex items-center justify-center">
                        <Package className="text-gray-400" size={36} />
                      </div>
                    )}
                  </div>

                  <div className="p-3">
                    <h3 className="text-sm font-bold text-gray-900 dark:!text-white mb-2 leading-5 min-h-[40px]">
                      {card.name}
                    </h3>

                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="rounded-lg bg-purple-50 dark:!bg-[#251a2b] p-2">
                        <div className="text-xs text-gray-500 dark:!text-gray-400">{t("profitManagementPage.card.sales")}</div>
                        <div className="text-sm font-bold text-[#9e68ba]">{card.soldQuantity.toFixed(0)}</div>
                      </div>
                      <div className="rounded-lg bg-blue-50 dark:!bg-[#1b2a3a] p-2">
                        <div className="text-xs text-gray-500 dark:!text-gray-400">{t("profitManagementPage.card.gross")}</div>
                        <div className="text-sm font-bold text-blue-600">{card.grossGain.toFixed(2)}</div>
                      </div>
                      <div className="rounded-lg bg-emerald-50 dark:!bg-[#153326] p-2">
                        <div className="text-xs text-gray-500 dark:!text-gray-400">{t("profitManagementPage.card.net")}</div>
                        <div className={`text-sm font-bold ${card.netGain >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {card.netGain.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 dark:!border-[#2f2f30] pt-3">
                      <p className="text-xs text-gray-500 dark:!text-gray-400 mb-2">
                        {t("profitManagementPage.card.unitCostsHint")}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t("profitManagementPage.fields.coast")}
                          value={cardCosts.coast || ""}
                          onChange={(e) => handleCostChange(card.id, "coast", e.target.value)}
                          className="rounded-lg border border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#141414] px-2 py-1.5 text-sm text-gray-900 dark:!text-white outline-none"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t("profitManagementPage.fields.purchasePrice")}
                          value={cardCosts.purchasePrice || ""}
                          onChange={(e) => handleCostChange(card.id, "purchasePrice", e.target.value)}
                          className="rounded-lg border border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#141414] px-2 py-1.5 text-sm text-gray-900 dark:!text-white outline-none"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t("profitManagementPage.fields.packagingPrice")}
                          value={cardCosts.packagingPrice || ""}
                          onChange={(e) => handleCostChange(card.id, "packagingPrice", e.target.value)}
                          className="rounded-lg border border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#141414] px-2 py-1.5 text-sm text-gray-900 dark:!text-white outline-none"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t("profitManagementPage.fields.deliveryPrice")}
                          value={cardCosts.deliveryPrice || ""}
                          onChange={(e) => handleCostChange(card.id, "deliveryPrice", e.target.value)}
                          className="rounded-lg border border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#141414] px-2 py-1.5 text-sm text-gray-900 dark:!text-white outline-none"
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:!text-gray-400">
                        {card.netGain >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        <span>{t("profitManagementPage.card.unitCost")}: {card.unitCost.toFixed(2)}</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSaveCosts(card.id)}
                        disabled={savingProductId === card.id}
                        className="mt-3 w-full rounded-lg bg-[#9e68ba] px-3 py-2 text-sm font-medium text-white hover:bg-[#8b59a7] transition-colors disabled:opacity-60"
                      >
                        {savingProductId === card.id
                          ? t("profitManagementPage.actions.saving")
                          : t("profitManagementPage.actions.save")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default ProfitManagement;